/*
 * Copyright (c) 2026 Bolmate B.V.
 *
 * Recovery from Cloudflare challenges that hit the PWA after a network switch.
 *
 * Symptom: while the installed (standalone) PWA is open and the device changes
 * network (home Wi-Fi <-> mobile LTE), the public IP changes. Cloudflare's
 * `cf_clearance` token is IP-bound, so on the new IP Cloudflare re-issues a
 * challenge. A background fetch/XHR in a standalone PWA cannot render or solve
 * an interactive challenge, so every API request starts failing (Cloudflare
 * returns an HTML challenge/block page, not our JSON) until the user manually
 * closes and reopens the app — a top-level navigation, which CAN pass the
 * challenge and refresh the clearance.
 *
 * This module detects such responses centrally (every request funnels through
 * bolmateFetch -> maintenanceHandler) and automates that recovery: a single,
 * debounced top-level reload re-passes the challenge for the user. A
 * sessionStorage-backed cap prevents a reload loop if the block is permanent
 * (hard firewall block rather than a solvable challenge) — after the cap we
 * stop reloading and tell the user to reopen the app.
 *
 * The real fix lives in Cloudflare config (don't challenge the API at all);
 * this is the client-side safety net so users switching Wi-Fi/LTE are not left
 * with a silently dead app.
 */

import i18n from '../i18n';

import {onShowConfirmSnackbar} from './renderUtil';

const RECOVERY_COUNT_KEY = 'bolmate-cf-recovery-count';
const RECOVERY_TS_KEY = 'bolmate-cf-recovery-ts';
// Reloads older than this are a *new* incident, not part of a loop — reset.
const RECOVERY_WINDOW_MS = 120_000;
// Max automatic reloads within the window before we give up and ask the user
// to reopen the app (guards against an unsolvable hard block looping forever).
const MAX_RELOADS = 2;

/**
 * True when `response` is a Cloudflare challenge/block rather than a
 * genuine response from our backend.
 */
export function isCloudflareChallenge(response?: Pick<Response, 'status' | 'headers'>): boolean {
    if (!response) return false;
    const headers = response.headers;
    // headers can be undefined in some browsers (observed in Firefox).
    if (!headers || typeof headers.get !== 'function') return false;

    // Cloudflare's explicit challenge marker. Match the value, not mere presence:
    // a hypothetical future `cf-mitigated: block` (unsolvable hard block) must NOT
    // be treated as a recoverable challenge and burn the reload budget here.
    return headers.get('cf-mitigated') === 'challenge';
}

function readNumber(key: string): number {
    try {
        return parseInt(sessionStorage.getItem(key) || '0', 10) || 0;
    } catch {
        return 0;
    }
}

/** Clear the reload backoff after a healthy response — a working session means
 *  any earlier incident is over, so a future block gets the full retry budget.
 *  Deliberately does NOT touch `recoveryScheduled`: that in-memory flag means "a
 *  reload is already committed (or we've given up) for this page life" and must
 *  only clear on an actual page reload. Resetting it here would re-open the guard
 *  during the brief delay before window.location.reload() fires, letting a second
 *  blocked request schedule another reload + snackbar and burn a retry slot. */
export function resetConnectionRecovery(): void {
    try {
        sessionStorage.removeItem(RECOVERY_COUNT_KEY);
        sessionStorage.removeItem(RECOVERY_TS_KEY);
    } catch {
        /* storage blocked — nothing to clear */
    }
}

function notifyReopenRequired(): void {
    onShowConfirmSnackbar(
        i18n.t(
            'pwa.connectionBlocked',
            'Verbinding geblokkeerd na netwerkwissel. Sluit de app volledig en open hem opnieuw.'
        ),
        {backgroundColor: 'color-blue-bolmate', textColor: 'white'}
    );
}

/** Schedule a single, capped top-level reload to re-pass the
 *  Cloudflare challenge. Exported for direct/manual use and testing. */
export function recoverFromConnectionBlock(): void {
    if (typeof window === 'undefined') return;

    const now = Date.now();
    const lastTs = readNumber(RECOVERY_TS_KEY);
    let count = readNumber(RECOVERY_COUNT_KEY);
    if (now - lastTs > RECOVERY_WINDOW_MS) count = 0;

    if (count >= MAX_RELOADS) {
        // Reloading already didn't help → likely a hard block. Lock the guard so
        // the dozens of parallel blocked requests on a network switch don't each
        // re-fire this branch (a wall of "reopen the app" snackbars); show once.
        notifyReopenRequired();
        return;
    }

    try {
        sessionStorage.setItem(RECOVERY_COUNT_KEY, String(count + 1));
        sessionStorage.setItem(RECOVERY_TS_KEY, String(now));
    } catch {
        /* storage blocked — in-memory guard still prevents a same-page loop */
    }
    window.location.reload();
}

/**
 * Central hook called for every response: recover on a Cloudflare challenge,
 * otherwise clear the backoff on a healthy response.
 */
export function handlePossibleConnectionBlock(response?: Response): void {
    if (isCloudflareChallenge(response)) {
        recoverFromConnectionBlock();
    } else if (response?.ok) {
        resetConnectionRecovery();
    }
}
